import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Clock, Timer, LogIn, LogOut, Coffee } from "lucide-react";
import OutsourceTimeTracker from "../components/OutsourceTimeTracker";
import "../styles/EmployeeAttendance.css";
import "../styles/OutsourcePortal.css";
import apiClient from "../api/api";
import { DateTime } from "luxon";
import OutsourceStatisticsPage from "../components/OutsourceStatisticsPage";



const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444"];

const OutsourceDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [statistics, setStatistics] = useState({
    today: 0,
    week: 0,
    month: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 600000); // Update every 10 minutes
    return () => clearInterval(timer);
  }, []);

  // Helper function to check if date is today (in London timezone)
  const isToday = (inputDate) => {
    try {
      const todayLondon = DateTime.now().setZone("Europe/London");
      const checkDateLondon = DateTime.fromISO(inputDate, { zone: "Europe/London" });

      return todayLondon.toISODate() === checkDateLondon.toISODate();
    } catch (error) {
      console.error("Error in isToday():", error);
      return false;
    }
  };

  // Helper function to check if date is in current week
  const isCurrentWeek = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    startOfWeek.setDate(today.getDate() - dayOfWeek + 1);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return checkDate >= startOfWeek && checkDate <= endOfWeek;
  };

  // Helper function to check if date is in current month
  const isCurrentMonth = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return (
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function for London timezone
  const getLondonTime = (date = new Date()) => {
    return new Date(
      date.toLocaleString("en-US", { timeZone: "Europe/London" })
    );
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "--:--";

    try {
      const londonTime = DateTime.fromISO(dateTime, { zone: "Europe/London" });
      return londonTime.toFormat("HH:mm");
    } catch (error) {
      console.error("Error formatting time:", error);
      return "--:--";
    }
  };

  const calculateTotalHours = (start, end) => {
    if (!start || !end) return 0;
    try {
      // Convert times to London timezone for consistent calculation, rounding start down, end up
      const startDt = DateTime.fromISO(start, { zone: "UTC" }).setZone("Europe/London");
      const startTime = startDt.startOf('minute').toJSDate();
      const endDt = DateTime.fromISO(end, { zone: "UTC" }).setZone("Europe/London");
      const endTime = endDt.plus({ minutes: endDt.second > 0 ? 1 : 0 }).startOf('minute').toJSDate();

      if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return 0;

      const diff = endTime - startTime;
      const totalHours = diff / (1000 * 60 * 60);

      // Prevent negative values in weekly/monthly statistics
      return Math.max(0, Math.round(totalHours * 100) / 100);
    } catch (error) {
      console.error("Error calculating total hours:", error);
      return 0;
    }
  };

  // Memoized fetch function
  const fetchData = async () => {
    setCurrentTime(new Date());
    try {
      const attendanceRes = await apiClient.get("/attendance");


      // Format Attendance Records
      const sortedAttendanceData = attendanceRes.data.sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );

      const formattedAttendance = sortedAttendanceData.map((record, index) => {
        const clockInDt = record.clock_in ? DateTime.fromISO(record.clock_in, { zone: "UTC" }).setZone("Europe/London") : null;
        const clockOutDt = record.clock_out ? DateTime.fromISO(record.clock_out, { zone: "UTC" }).setZone("Europe/London") : null;

        return {
          id: record.id || index + 1,
          date: DateTime.fromISO(record.date, { zone: "UTC" }).setZone("Europe/London").toFormat("dd/MM/yyyy"),
          rawDate: record.date,
          CheckIn: clockInDt ? clockInDt.toFormat("HH:mm") : "--:--",
          CheckOut: clockOutDt ? clockOutDt.minus({ minutes: 1 }).toFormat("HH:mm") : "--:--",
          clockInRaw: record.clock_in,
          clockOutRaw: record.clock_out,
          totalHours: calculateTotalHours(record.clock_in, record.clock_out),
        };
      });


      setAttendanceRecords(formattedAttendance);

      // Calculate Statistics
      const todayRecord = formattedAttendance.find((record) =>
        isToday(record.clockInRaw)
      );

      const weekRecords = formattedAttendance.filter((record) =>
        isCurrentWeek(record.rawDate)
      );
      const monthRecords = formattedAttendance.filter((record) =>
        isCurrentMonth(record.rawDate)
      );

      const todayHours = todayRecord ? todayRecord.totalHours : 0;
      const weekHours = weekRecords.reduce(
        (sum, record) => sum + record.totalHours,
        0
      );
      const monthHours = monthRecords.reduce(
        (sum, record) => sum + record.totalHours,
        0
      );

      setStatistics({
        today: todayHours,
        week: weekHours,
        month: monthHours,
      });

      // Generate Chart Data for Last 7 Days
      const getLast7Days = () => {
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          days.push(new Date(d));
        }
        return days;
      };

      const last7Dates = getLast7Days();

      const weeklyChartData = last7Dates.map((date) => {
        const dayName = date.toLocaleDateString("en-US", {
          weekday: "short",
        });

        const dayRecord = formattedAttendance.find((record) => {
          const recordDate = new Date(record.rawDate);
          return recordDate.toDateString() === date.toDateString();
        });

        const clampedHours = Math.max(0, dayRecord ?
          calculateTotalHours(dayRecord.clockInRaw, dayRecord.clockOutRaw) : 0);

        return {
          day: dayName,
          hours: clampedHours, // Guaranteed >= 0
          date: date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
          }),
        };
      });

      setWeeklyData(weeklyChartData);
    } catch (err) {
      console.error("Failed to fetch attendance data:", err);
      setAttendanceRecords([]);
      setWeeklyData([]);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, []);

  // Build Recent Activity from today's attendance data
  useEffect(() => {
    const activity = [];
    const processedRecords = new Set(); // Track processed record IDs to avoid duplicates

    const todayRecords = attendanceRecords.filter((record) =>
      isToday(record.clockInRaw)
    );

    todayRecords.forEach((record) => {
      // Skip if this record ID has already been processed
      if (processedRecords.has(record.id)) {
        return;
      }

      if (record.clockInRaw) {
        activity.push({
          id: `checkin-${record.id}`,
          action: "Check In",
          time: record.CheckIn,
          type: "in",
          avatar: "IN",
          timestamp: new Date(record.clockInRaw),
        });
      }

      if (record.clockOutRaw) {
        activity.push({
          id: `checkout-${record.id}`,
          action: "Check Out",
          time: record.CheckOut,
          type: "out",
          avatar: "OUT",
          timestamp: new Date(record.clockOutRaw),
        });
      }

      // Mark this record as processed
      processedRecords.add(record.id);
    });

    // Sort activities by timestamp (chronological order - earliest first)
    activity.sort((a, b) => a.timestamp - b.timestamp);
    setRecentActivity(activity);
  }, [attendanceRecords]);

  const ActivityItem = ({ action, time, type, avatar }) => {
    const getIconAndColor = () => {
      switch (type) {
        case "in":
          return { icon: LogIn, colorClass: "green" };
        case "out":
          return { icon: LogOut, colorClass: "red" };
        case "break":
          return { icon: Coffee, colorClass: "yellow" };
        default:
          return { icon: Clock, colorClass: "blue" };
      }
    };

    const { icon: Icon, colorClass } = getIconAndColor();

    return (
      <div className="activity-item">
        <div className={`activity-icon ${colorClass}`}>
          <Icon size={16} />
        </div>
        <div className="activity-content">
          <p className="activity-action">{action}</p>
          <p className="activity-time">{time}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="out-attendance-app">
      <div className="out-main-content">
        {/* Top section */}
        <div className="out-top-section">
          <OutsourceTimeTracker />
          <div className="out-weekly-hours">
            <h2 className="card-title">Weekly Hours</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [`${value}h`, "Hours Worked"]}
                  labelFormatter={(label) => {
                    const dayData = weeklyData.find((d) => d.day === label);
                    return `${label} (${dayData?.date})`;
                  }}
                />
                <Bar dataKey="hours" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="out-dashboard-grid">
          <OutsourceStatisticsPage />
          <div className="out-recent-activity">
            <h2 className="card-title">Today's Activity</h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {recentActivity.length > 0 ? (
                recentActivity
                  .slice(0, 6)
                  .map((activity) => (
                    <ActivityItem key={activity.id} {...activity} />
                  ))
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "#9CA3AF",
                    padding: "1rem",
                  }}
                >
                  No activity recorded for today
                </p>
              )}
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="card out-how-it-works">
          <h2 className="card-title">How It Works</h2>
          <div className="out-how-it-works-content">
            <div className="out-how-it-works-item">
              <div className="out-how-it-works-number">1</div>
              <div className="out-how-it-works-text">
                <h4>Clock In</h4>
                <p>Click the "Clock In" button when you start your work session. Your session will begin immediately.</p>
              </div>
            </div>

            <div className="out-how-it-works-item">
              <div className="out-how-it-works-number">2</div>
              <div className="out-how-it-works-text">
                <h4>Track Your Time</h4>
                <p>The timer shows your current session duration. Progress bar indicates your work session status.</p>
              </div>
            </div>

            <div className="out-how-it-works-item">
              <div className="out-how-it-works-number">3</div>
              <div className="out-how-it-works-text">
                <h4>Clock Out</h4>
                <p>When finished, click "Clock Out" to end your session. Your total hours will be recorded.</p>
              </div>
            </div>

            <div className="out-how-it-works-item">
              <div className="out-how-it-works-number">4</div>
              <div className="out-how-it-works-text">
                <h4>View Reports</h4>
                <p>Check your attendance records and weekly/monthly statistics in the dashboard.</p>
              </div>
            </div>

            <div className="out-how-it-works-item">
              <div className="out-how-it-works-number">5</div>
              <div className="out-how-it-works-text">
                <h4>Multiple Sessions</h4>
                <p>You can clock in/out multiple times per day. All sessions are tracked separately.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutsourceDashboard;
